/* eslint-disable no-nested-ternary */
import React, { createContext, useMemo } from "react";

import { useQueries } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { useAppState } from "../../../context/StateContext";
import { Carrier } from "../../../models/carrier";
import { Customer } from "../../../models/customer";
import { Document } from "../../../models/document";
import { Milestone } from "../../../models/milestone";
import { Port } from "../../../models/port";
import { Project } from "../../../models/project";
import { Supplier } from "../../../models/supplier";
import CarrierService from "../../../services/carrier.service";
import CustomerService from "../../../services/customer.service";
import DocumentService from "../../../services/document.service";
import PortService from "../../../services/port.service";
import ProjectService from "../../../services/project.service";
import ShipmentService from "../../../services/shipment.service";
import SupplierService from "../../../services/supplier.service";
import { Status } from "../../../util/enums";
import {
  CarrierKeys,
  CustomerKeys,
  DocumentKeys,
  MilestoneKeys,
  PortKeys,
  ProjectKeys,
  ShipmentResourceKeys,
  SupplierKeys,
} from "../../../util/queries";

export type ExternalList = {
  resourceCompanyId?: number;
  customerList: Customer[];
  milestoneDates: Milestone[];
  companyDocuments: Document[];
  portList: Port[];
  supplierList: Supplier[];
  projectList: Project[];
  carrierList: Carrier[];
};

interface ShipmentResourcesContextProps extends ExternalList {
  loading: boolean;
}

const ShipmentResourcesContext = createContext<ShipmentResourcesContextProps>(
  {} as ShipmentResourcesContextProps
);

interface ShipmentResourcesProviderProps {
  children: React.ReactChild;
}

type Request = Array<
  [key: any, execute: (args: any) => Promise<AxiosResponse<any, any>>, ...executeArgs: any]
>;

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const CACHE_TIME = 5 * 60 * 60 * 1000; // 5 horas

const ShipmentResourcesProvider = ({ children }: ShipmentResourcesProviderProps) => {
  const { currentUser } = useAppState();

  const companyId = useMemo(() => {
    return currentUser?.companyId;
  }, []);

  const Resources: Request = useMemo(() => {
    return [
      [
        CustomerKeys.all,
        CustomerService.getAll,
        { sortBy: JSON.stringify([["id", "ASC"]]), status: Status.ACTIVE, companyId },
      ],
      [MilestoneKeys.all, ShipmentService.getMilestoneDates, { status: Status.ACTIVE, companyId }],
      [DocumentKeys.all, DocumentService.getAll, { companyId: "", status: Status.ACTIVE }],
      [
        PortKeys.all,
        PortService.getAll,
        {
          attributes: JSON.stringify(["id", "site", "siteCode", "city", "countryId"]),
          sortBy: JSON.stringify([["siteCode", "ASC"]]),
          include: JSON.stringify([
            { association: "country", attributes: ["code"] },
            { association: "companyPorts", required: true, where: { companyId } },
          ]),
        },
      ],
      [
        SupplierKeys.getAll([]),
        SupplierService.getAll,
        {
          status: [Status.ACTIVE],
          companyId,
          include: JSON.stringify([{ association: "associatedCompany" }]),
        },
      ],
      [
        ProjectKeys.all,
        ProjectService.getAll,
        {
          sortBy: JSON.stringify([["name", "ASC"]]),
          status: Status.ACTIVE,
          include: JSON.stringify([
            { association: "userProjects", where: { userId: currentUser?.id } },
          ]),
        },
      ],
      [
        CarrierKeys.all,
        CarrierService.getAll,
        {
          sortBy: JSON.stringify([["name", "ASC"]]),
          status: Status.ACTIVE,
          companyId,
        },
      ],
    ];
  }, [companyId]);

  const results = useQueries({
    queries: Resources.map(([key, execute, args, enabled = !!companyId]) => {
      return {
        queryKey: ShipmentResourceKeys.getResource(key, args),
        queryFn: async () => {
          const response = await execute(args);

          return response.data;
        },
        refetchOnWindowFocus: false,
        enabled,
        placeholderData: [],
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
      };
    }),
  });

  const [
    customerQuery,
    milestoneQuery,
    documentsQuery,
    portQuery,
    supplierQuery,
    projectQuery,
    carrierQuery,
  ] = results;

  const loadingQueries = results.some((result) => result.isRefetching);

  return (
    <ShipmentResourcesContext.Provider
      value={{
        resourceCompanyId: companyId,
        customerList: customerQuery.data,
        milestoneDates: milestoneQuery.data,
        companyDocuments: documentsQuery.data,
        supplierList: supplierQuery.data,
        portList: portQuery.data,
        projectList: projectQuery.data,
        carrierList: carrierQuery.data,
        loading: loadingQueries,
      }}
    >
      {children}
    </ShipmentResourcesContext.Provider>
  );
};

export { ShipmentResourcesContext, ShipmentResourcesProvider };
